/* GreenFox Series Commerce - Wholesale quantity + price break logic */

class GfSeriesSelector extends HTMLElement {
  connectedCallback() {
    const section = this.closest('[data-section-id]');
    if (!section) return;

    this.section = section;
    this.sectionId = section.dataset.sectionId;
    this.qtyInput = this.querySelector('.gf-quantity__input');
    this.totalEl = this.querySelector('[data-series-total]');
    this.priceBreakRows = section.querySelectorAll('.gf-price-breaks__row');

    /* Parse price breaks from data attribute */
    try {
      const breaksAttr = this.dataset.priceBreaks;
      const parsed = breaksAttr ? JSON.parse(breaksAttr) : [];
      const arr = Array.isArray(parsed) ? parsed : [];
      this.priceBreaks = arr.sort((a, b) => (a.min || 0) - (b.min || 0));
    } catch {
      this.priceBreaks = [];
    }

    /* Parse unit price from variant */
    this.basePrice = parseFloat(this.dataset.basePrice) || 0;
    this.perSeries = parseInt(this.dataset.perSeries, 10) || 1;

    if (this.qtyInput) {
      this.qtyInput.addEventListener('change', () => {
        this.clampQuantity();
        this.update();
      });

      /* Clamp quantity on form submit to enforce min/max */
      const form = this.qtyInput.form || section.querySelector('form[data-type="add-to-cart-form"]');
      if (form) {
        form.addEventListener('submit', () => this.clampQuantity());
      }

      this.update();
    }

    /* Listen for variant changes to update base price */
    const variantInput = section.querySelector('input.product-variant-id');
    if (variantInput) {
      const jsonEl = document.getElementById(`gf-product-variants-${this.sectionId}`);
      this.allVariants = jsonEl ? JSON.parse(jsonEl.textContent) : [];

      this._variantObserver = new MutationObserver(() => {
        const vid = variantInput.value;
        const v = this.allVariants.find((x) => String(x.id) === vid);
        if (v) {
          this.basePrice = v.price / 100;
          this.update();
        }
      });
      this._variantObserver.observe(variantInput, { attributes: true, attributeFilter: ['value'] });
      this._variantInput = variantInput;
      this._variantChangeHandler = () => {
        const vid = variantInput.value;
        const v = this.allVariants.find((x) => String(x.id) === vid);
        if (v) {
          this.basePrice = v.price / 100;
          this.update();
        }
      };
      variantInput.addEventListener('change', this._variantChangeHandler);
    }
  }

  disconnectedCallback() {
    if (this._variantObserver) this._variantObserver.disconnect();
    if (this._variantInput && this._variantChangeHandler) {
      this._variantInput.removeEventListener('change', this._variantChangeHandler);
    }
  }

  update() {
    const qty = parseInt(this.qtyInput.value, 10) || 1;
    const applicableTier = this.getApplicableTier(qty);
    const unitPrice = applicableTier ? applicableTier.price : this.basePrice;
    const total = qty * unitPrice;
    const totalPieces = qty * this.perSeries;

    /* Update total display: price + piece count */
    if (this.totalEl) {
      const priceStr = this.formatMoney(total * 100);
      this.totalEl.textContent = `${priceStr} (${totalPieces} ${this.totalEl.dataset.piecesLabel || 'adet'})`;
    }

    /* Highlight active price break row */
    this.priceBreakRows.forEach((row) => {
      const tierMin = parseInt(row.dataset.tierMin, 10);
      const isActive = applicableTier && tierMin === applicableTier.min;
      row.classList.toggle('gf-price-breaks__row--active', isActive);
    });
  }

  clampQuantity() {
    const min = parseInt(this.qtyInput.min, 10) || 1;
    const max = this.qtyInput.max ? parseInt(this.qtyInput.max, 10) : null;
    let val = parseInt(this.qtyInput.value, 10) || min;
    if (val < min) val = min;
    if (max && val > max) val = max;
    this.qtyInput.value = val;
  }

  getApplicableTier(qty) {
    let applicable = null;
    for (const tier of this.priceBreaks) {
      if (qty >= tier.min) {
        applicable = tier;
      }
    }
    return applicable;
  }

  formatMoney(cents) {
    const moneyFormat = this.section.dataset.moneyFormat || '{{amount}}';
    const amount = (cents / 100).toFixed(2);
    const amountNoDecimals = String(Math.round(cents / 100));
    const amountComma = amount.replace('.', ',');

    const amountApostrophe = amount.replace(/(\d)(?=(\d{3})+\.)/g, "$1'").replace('.', '.');
    const amountSpaceSep = amountNoDecimals.replace(/(\d)(?=(\d{3})+$)/g, '$1 ');

    let formatted = moneyFormat
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountComma)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, amountNoDecimals.replace(/(\d)(?=(\d{3})+$)/g, '$1.'))
      .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, amountApostrophe)
      .replace(/\{\{\s*amount_no_decimals_with_space_separator\s*\}\}/g, amountSpaceSep)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
      .replace(/\{\{\s*amount\s*\}\}/g, amount);

    return formatted.replace(/<[^>]*>/g, '');
  }
}

if (!customElements.get('gf-series-selector')) {
  customElements.define('gf-series-selector', GfSeriesSelector);
}
